import { useMemo, useState } from 'react';
import type { LocalTrayState } from '../lib/types';

interface Props {
  state: LocalTrayState;
  onFilter?: (emotion: string | null) => void;
}

export function CognitionLens({ state, onFilter }: Props) {
  const [showDetector, setShowDetector] = useState(false);
  const seedlings = state.seedlings.filter(s => !s.retracted_at);

  // Local, crude, fully transparent pattern detector — runs in browser, no data leaves.
  const localPatterns = useMemo(() => {
    if (seedlings.length < 2) return null;

    const texts = seedlings.map(s => (s.content + ' ' + (s.impact || '') + ' ' + (s.accountability || '')).toLowerCase());

    // Naive co-occurring actors / roles
    const actorMentions = new Map<string, number>();
    seedlings.forEach(s => {
      if (s.accountability) {
        const words = s.accountability.toLowerCase().split(/\W+/);
        words.forEach(w => {
          if (w.length > 3 && !['will','can','the','and','for','that','this','with'].includes(w)) {
            actorMentions.set(w, (actorMentions.get(w) || 0) + 1);
          }
        });
      }
    });

    const topActors = Array.from(actorMentions.entries()).sort((a, b) => b[1] - a[1]).slice(0, 4);

    // Very naive "loop" detection
    const loops: string[] = [];
    if (texts.some(t => /loop|cycle|again|repeat/.test(t))) loops.push('repeating decision path mentioned in 1+ seedlings');
    if (texts.some(t => /time zone|timezone|excluded|absent/.test(t))) loops.push('scheduling / presence exclusion pattern');
    if (texts.some(t => /bill|paid|cost|carried/.test(t))) loops.push('invisible recurring cost carried by one node');

    return {
      topActors,
      loops: loops.length ? loops : ['no obvious textual repetition in the short fragments'],
      totalSeedlings: seedlings.length,
      withSpecificAccountability: seedlings.filter(s => s.accountability && s.accountability.trim().length > 12).length,
    };
  }, [seedlings]);

  const avgRepair = seedlings.length
    ? Math.round(seedlings.reduce((sum, s) => sum + (s.repair_readiness ?? 40), 0) / seedlings.length)
    : 0;

  const emotionSpread = useMemo(() => {
    const counts = new Map<string, number>();
    seedlings.forEach(s => {
      (s.emotion_tags || []).forEach(e => counts.set(e, (counts.get(e) || 0) + 1));
    });
    return Array.from(counts.entries()).sort((a, b) => b[1] - a[1]).slice(0, 5);
  }, [seedlings]);

  return (
    <div className="rounded-3xl border border-[var(--border)] bg-[var(--bg-elev)] p-5 text-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="uppercase tracking-[2px] text-[10px] text-[var(--text-subtle)]">cognition lens • model limitations visible</div>
          <div className="font-medium mt-0.5">What the tray actually captured vs what any view can claim to know</div>
        </div>
        <button onClick={() => setShowDetector(v => !v)} className="text-xs px-3 py-1 rounded-xl border border-[var(--border)] hover:bg-[var(--bg)]">
          {showDetector ? 'hide' : 'run local pattern detector'}
        </button>
      </div>

      <div className="mt-4 grid md:grid-cols-2 gap-x-6 gap-y-4 text-[13px]">
        <div className="space-y-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[var(--text-subtle)] mb-1">Captured as first-class data</div>
            <ul className="space-y-1 text-[var(--text-muted)] text-xs">
              <li>• Repair readiness (0–100) on {seedlings.filter(s => s.repair_readiness != null).length} seedlings — avg {avgRepair}</li>
              <li>• Intention / Impact separation recorded on {seedlings.filter(s => s.intention || s.impact).length} entries</li>
              <li>• Accountability specificity (who + exact observable action) on {seedlings.filter(s => s.accountability).length} entries</li>
              <li>• Follow-ups (actor + action + timeframe) on {seedlings.filter(s => s.followups && s.followups.length).length} entries</li>
              <li>• Emotion tags chosen from the 24-petal granularity wheel</li>
            </ul>
          </div>
          <div className="text-[10px] text-[var(--text-subtle)] border-l-2 pl-3 border-[var(--border)]">
            This is the cognition asset. It lives only inside this tray. Export it. Print it. The wheel and lens are just different views of the same raw fragments.
          </div>
        </div>

        <div className="space-y-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[var(--text-subtle)] mb-1">Visible limitations (always)</div>
            <div className="text-xs text-[var(--text-muted)] leading-snug">
              • The wheel uses only the emotion tags you and your people explicitly chose (plus a tiny local keyword assist shown below).<br />
              • No model has read your full context, power dynamics, culture, or what was never written.<br />
              • “Local pattern detector” is crude frequency + regex running entirely in your browser. It will hallucinate plausible connections from small n.<br />
              • High repair readiness number does not mean the system is actually ready — it means the person who planted felt that number at that moment.
            </div>
          </div>

          {emotionSpread.length > 0 && (
            <div className="text-xs">
              <span className="text-[var(--text-subtle)]">Top textures right now:</span>{' '}
              {emotionSpread.map(([e, c], i) => (
                <button key={i} onClick={() => onFilter && onFilter(e)} className="underline decoration-dotted mr-2 hover:text-[var(--accent)]">
                  {e} ({c})
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {showDetector && localPatterns && (
        <div className="mt-5 rounded-2xl border border-[var(--rust)]/40 bg-[#FBF7EF] p-4 text-xs">
          <div className="font-medium text-[var(--rust-warm)] mb-2">LOCAL PATTERN DETECTOR — client-side only, zero external call</div>
          <div className="grid md:grid-cols-2 gap-x-5 gap-y-3">
            <div>
              <div className="text-[var(--text-subtle)] mb-0.5">Frequent accountability actors (naive word count)</div>
              {localPatterns.topActors.length ? localPatterns.topActors.map(([w, c], i) => (
                <div key={i}>• {w} <span className="text-[var(--text-subtle)]">({c})</span></div>
              )) : <div className="text-[var(--text-subtle)]">No clear actor language yet.</div>}
            </div>
            <div>
              <div className="text-[var(--text-subtle)] mb-0.5">Possible repeating structures (very crude)</div>
              {localPatterns.loops.map((l, i) => <div key={i}>• {l}</div>)}
              <div className="mt-1 text-[10px] text-[var(--text-subtle)]">Specificity rate: {localPatterns.withSpecificAccountability}/{localPatterns.totalSeedlings} seedlings name a concrete who + action.</div>
            </div>
          </div>
          <div className="mt-3 pt-3 border-t border-[var(--border)] text-[10px] text-[var(--text-subtle)]">
            This detector only sees the text fragments. It does not know silence, who was not invited, or what power made some things unsayable. Treat every suggestion as a prompt to go back to the raw seedlings, not as insight.
          </div>
        </div>
      )}

      <div className="mt-4 text-[10px] text-[var(--text-subtle)]">
        If a future version adds Workers AI for “possible systems connections”, every result will be wrapped in an identical limitations card naming the exact model, context window, and the fact that it only ever saw the two snippets you fed it.
      </div>
    </div>
  );
}
